"use client";

import { useEffect, useState, useCallback } from "react";
import Cookies from "js-cookie";

interface UserProfile {
  id: string;
  fullname: string;
  slug?: string;
  avatar?: {
    contentUrl: string;
  };
}

interface UserCookie {
  id?: string;
  slug?: string;
  fullname?: string;
  avatar?: string | null;
}

function readUserCookie(): UserProfile | null {
  const raw = Cookies.get("user");
  if (!raw) return null;

  try {
    const parsed: UserCookie = JSON.parse(raw);
    if (!parsed.id) return null;

    return {
      id: parsed.id,
      fullname: parsed.fullname ?? parsed.slug ?? "",
      slug: parsed.slug,
      avatar: parsed.avatar ? { contentUrl: parsed.avatar } : undefined,
    };
  } catch {
    console.warn("[AuthSync] Failed to parse user cookie:", raw);
    return null;
  }
}

function readAccessTokenCookie(): string | null {
  return Cookies.get("access_token") ?? null;
}

export function useAuthSync() {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const syncAuth = useCallback(() => {
    const profile = readUserCookie();
    const token = readAccessTokenCookie();
    setUser(profile);
    setAccessToken(token);
    setIsLoading(false);
  }, []);

  const logout = useCallback(() => {
    // Remove the shared cookies immediately so the UI updates on this page.
    const domain = process.env.NEXT_PUBLIC_COOKIE_DOMAIN || "localhost";
    Cookies.remove("user", { path: "/", domain });
    Cookies.remove("access_token", { path: "/", domain });
    setUser(null);
    setAccessToken(null);

    // Clean up the front app's OIDC localStorage in the background via a hidden
    // iframe — no visible redirect for the user.
    const appUrl = process.env.NEXT_PUBLIC_APP_URL || "https://communo.app";
    const iframe = document.createElement("iframe");
    iframe.style.cssText = "display:none;width:0;height:0;position:absolute;";
    iframe.src = `${appUrl}/auth-logout-bridge.html`;

    const cleanup = () => {
      window.removeEventListener("message", onMessage);
      if (document.body.contains(iframe)) document.body.removeChild(iframe);
    };

    const onMessage = (e: MessageEvent) => {
      if (e.data?.type === "communo:logout-done") cleanup();
    };

    window.addEventListener("message", onMessage);
    // Safety net: remove the iframe after 4s even if no response.
    setTimeout(cleanup, 4000);
    document.body.appendChild(iframe);
  }, []);

  useEffect(() => {
    // Cookies are only readable on the client: the server render must show the
    // loading state, then this one-shot sync hydrates auth after mount.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    syncAuth();
  }, [syncAuth]);

  return {
    user,
    accessToken,
    isLoading,
    isAuthenticated: !!user,
    refresh: syncAuth,
    logout,
  };
}
